import {useLazyQuery} from '@apollo/client';
import {useCallback} from 'react';
import {
    GET_REVIEW_TASK_LIST,
    GetReviewTaskListResponse,
    GetReviewTaskListVariables,
} from '../graphql/queries/getReviewTaskList';
import {
    ReviewBizType,
    ReviewTaskFiltersTypeInput,
    ReviewTaskListItem,
} from '../graphql/types/ReviewTaskType';

/** Page 层组装好的列表筛选输入（不含 bizType / statuses，由 hook 兜底补齐） */
export type ListSharedFilters = Omit<ReviewTaskFiltersTypeInput, 'bizType' | 'statuses'>;

export interface ReviewTaskQueryResult {
    data: ReviewTaskListItem[];
    totalElements: number;
    loading: boolean;
    error?: unknown;
    executeQuery: (
        sharedFilters: ListSharedFilters,
        statuses: string[] | undefined,
        page: number,
        size: number,
        sortDirection?: 'ASC' | 'DESC'
    ) => void;
}

/**
 * 审核工作台列表查询 hook
 * 一期固定 bizType = CUSTOMER_COMPLAINT；其他 bizType 接入后再扩展
 * 注意：列表查询必须把所有筛选条件都下传给后端，否则会出现「Tab 数字与表格行数不一致」
 */
export const useReviewTaskQuery = (
    bizType: string = ReviewBizType.CUSTOMER_COMPLAINT
): ReviewTaskQueryResult => {
    const [execute, {data, loading, error}] = useLazyQuery<
        GetReviewTaskListResponse,
        GetReviewTaskListVariables
    >(GET_REVIEW_TASK_LIST, {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
    });

    const executeQuery = useCallback(
        (
            sharedFilters: ListSharedFilters,
            statuses: string[] | undefined,
            page: number,
            size: number,
            sortDirection?: 'ASC' | 'DESC'
        ) => {
            execute({
                variables: {
                    filters: {
                        ...sharedFilters,
                        bizType,
                        statuses: statuses && statuses.length > 0 ? statuses : null,
                    },
                    // sortDirection 仅对提交时间列生效；不传时后端默认 ASC（FIFO）
                    pageRequest: {page, size, sortDirection},
                },
            });
        },
        [execute, bizType]
    );

    return {
        data: data?.getReviewTaskPageList?.content ?? [],
        totalElements: data?.getReviewTaskPageList?.totalElements ?? 0,
        loading,
        error,
        executeQuery,
    };
};
